import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    type UpdateAutoplayModuleMutation,
    type UpdateAutoplayModuleMutationVariables,
    UpdateAutoplayModuleDocument,
} from '../../../../api/mutations/updateAutoplayModule';
import { type AutoplayModulePayload } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type FormValues = AutoplayModulePayload;

type updatePayGatePaymentModuleFormProps = {
    moduleId: string;
    initialValues: FormValues;
    onComplete: () => void;
};

const loadingMessageKey = 'update-autoplay-module-loading';

const UpdateAutoplayModuleForm = ({ moduleId, initialValues, onComplete }: updatePayGatePaymentModuleFormProps) => {
    const { t } = useTranslation(['autoplayModuleDetails']);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('autoplayModuleDetails:autoplayModuleDrawer.messages.submitting'),
                key: loadingMessageKey,
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateAutoplayModuleMutation, UpdateAutoplayModuleMutationVariables>({
                    mutation: UpdateAutoplayModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy(loadingMessageKey);
                });

            message.success({
                content: t('autoplayModuleDetails:autoplayModuleDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [moduleId, apolloClient, onComplete, t]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updateAutoplayModule"
                    layout="vertical"
                    name="updateAutoplayModule"
                    onSubmitCapture={handleSubmit}
                >
                    <InputField
                        {...t('autoplayModuleDetails:autoplayModuleDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default UpdateAutoplayModuleForm;
